import React from 'react';
import { Divider, IconButton, Link, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Skeleton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import { useAuth } from '../../hooks';
import { useNavigation } from './NavigationContext';
import { NavigationErrorBoundary } from './NavigationErrorBoundary';

const drawerWidth = 220;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(4)})`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(6)})`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 0.5),
    ...theme.mixins.toolbar,
    minHeight: '60px!important',
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

export interface NavigationDrawerProps {
    open: boolean;
    onToggle: () => void;
}

const NavigationDrawerContent: React.FC<NavigationDrawerProps> = ({ open, onToggle }) => {
    const { user } = useAuth();
    const { navItems: items, hasFetched: hasNavBarResponse } = useNavigation();

    return (
        <Drawer variant='permanent' open={open}>
            <DrawerHeader>
                <IconButton onClick={onToggle}>{<ChevronLeftIcon />}</IconButton>
            </DrawerHeader>
            <Divider />
            {user && (
                <List>
                    {hasNavBarResponse
                        ? items
                            ? items.map(item => {
                                  const href =
                                      item.url +
                                      (item.url.includes(window.location.hostname) ? '' : `?t=${user.accessToken}&r=${user.refreshToken}`);
                                  return (
                                      <ListItem
                                          key={item.title}
                                          disablePadding
                                          sx={{
                                              display: 'block',
                                              '& .MuiListItemButton-root': {
                                                  '&.Mui-selected': {
                                                      backgroundColor: 'rgba(66, 196, 171, 0.2)',
                                                  },
                                              },
                                          }}
                                          title={item.title}>
                                          <Link href={href} underline={'none'} color={'inherit'}>
                                              <ListItemButton
                                                  selected={window.location.href.includes(item.url)}
                                                  sx={{
                                                      minHeight: 10,
                                                      justifyContent: open ? 'initial' : 'center',
                                                      px: 1.125,
                                                      py: 0.125,
                                                      '& .MuiListItemButton-root': {
                                                          '& .Mui-selected': {
                                                              backgroundColor: 'red!important',
                                                          },
                                                      },
                                                  }}>
                                                  <ListItemIcon
                                                      className={'nav-icon-svg'}
                                                      sx={{
                                                          minWidth: 0,
                                                          mr: open ? 1 : 'auto',
                                                          justifyContent: 'center',
                                                      }}
                                                      dangerouslySetInnerHTML={{ __html: item.icon }}
                                                  />
                                                  <ListItemText primary={item.title} sx={{ opacity: open ? 1 : 0 }} />
                                              </ListItemButton>
                                          </Link>
                                      </ListItem>
                                  );
                              })
                            : null
                        : Object.keys(Array(10).fill(1)).map(item => (
                              <ListItem key={'skeleton-nav-' + item} disablePadding sx={{ display: 'block' }}>
                                  <ListItemButton
                                      sx={{
                                          minHeight: 10,
                                          justifyContent: open ? 'initial' : 'center',
                                          px: 2.5,
                                          py: 0.25,
                                      }}>
                                      <ListItemIcon
                                          className={'nav-icon-svg'}
                                          sx={{
                                              minWidth: 0,
                                              mr: open ? 1 : 'auto',
                                              justifyContent: 'center',
                                          }}>
                                          <Skeleton variant='circular' width={'22px'} height={'22px'} />
                                      </ListItemIcon>
                                      <ListItemText
                                          primary={<Skeleton variant='rectangular' width={'169px'} height={'23px'} />}
                                          sx={{ opacity: open ? 1 : 0 }}
                                      />
                                  </ListItemButton>
                              </ListItem>
                          ))}
                </List>
            )}
        </Drawer>
    );
};

export const NavigationDrawer: React.FC<NavigationDrawerProps> = (props) => (
    <NavigationErrorBoundary>
        <NavigationDrawerContent {...props} />
    </NavigationErrorBoundary>
);
